import { Table, Tag, Tooltip, Typography } from "antd";
import mergeClassNames from "common/mergeClassNames";
import DataObjectLink from "components/atoms/DataObjectLink";
import React from "react";
import HighlightValue from "./HighlightValue";
import "./style.css";

const { Text } = Typography;
const { Column } = Table;

interface IDataObjectSearchResultListProps {
    loading: boolean;
    hits: IDataObjectSearchResultHit[];
    totalHits?: number;
    className?: string;
    onDataObjectSelected?: (d: IDataObjectRecord) => void;
    onPageSelect?: (p: IPagination) => void;
}

export default function DataObjectSearchResultList({ loading, hits, totalHits = 0, className = "", onDataObjectSelected, onPageSelect }: IDataObjectSearchResultListProps) {
    const [highlightColumns, setHighlightColumns] = React.useState<{
        tagColumns: string[];
        indexColumnCount: number;
    }>({
        tagColumns: [],
        indexColumnCount: 0,
    });
    const [populatedHits, setPopulatedHists] = React.useState<IDataObjectSearchResultHit[]>([]);

    React.useEffect(() => {
        return () => {
            setHighlightColumns({
                tagColumns: [],
                indexColumnCount: 0,
            });
            setPopulatedHists([]);
        };
    }, []);

    React.useEffect(() => {
        let tagColumns = new Set<string>();
        let indexColumnCount = 0;
        const generatedHits: IDataObjectSearchResultHit[] = [];

        if (hits && hits.length > 0) {
            for (let hit of hits) {
                if (hit.highlight) {
                    const newHit: IDataObjectSearchResultHit = {
                        ...hit,
                    };
                    let hitKeys = Object.keys(hit.highlight);
                    let nonTagColumnCount = 0;

                    // Loop through all keys
                    for (let i = 0; i < hitKeys.length; i++) {
                        // For Array type highlight
                        if (Array.isArray(hit.highlight[hitKeys[i]])) {
                            if (hitKeys[i].startsWith("#")) {
                                // Check if it's tag (tag begins with #)
                                tagColumns.add(hitKeys[i]);
                                newHit.highlight[hitKeys[i]] = hit.highlight[hitKeys[i]];
                            } else {
                                // Non-tag
                                newHit.highlight[nonTagColumnCount] = hit.highlight[hitKeys[i]];
                                nonTagColumnCount = nonTagColumnCount + 1;
                            }
                        } else {
                            const hightlight = hit.highlight[hitKeys[i]] as IHighlightObject;
                            if (hightlight.tags?.length > 0) {
                                for (let tag of hightlight.tags) {
                                    tagColumns.add(tag);
                                    newHit.highlight[hitKeys[i]] = [
                                        {
                                            [hitKeys[i]]: hightlight.values,
                                        },
                                    ];
                                }
                            } else {
                                newHit.highlight[nonTagColumnCount] = [
                                    {
                                        [hitKeys[i]]: hightlight.values,
                                    },
                                ];
                                nonTagColumnCount = nonTagColumnCount + 1;
                            }
                        }
                    }

                    indexColumnCount = Math.max(indexColumnCount, nonTagColumnCount);
                    generatedHits.push(newHit);
                } else {
                    // No highlight
                    generatedHits.push({ ...hit });
                }
            }
        }
        setHighlightColumns({
            tagColumns: Array.from(tagColumns),
            indexColumnCount,
        });
        setPopulatedHists(generatedHits);
    }, [hits]);

    const onClickRow = (record: IDataObjectSearchResultHit) => {
        return {
            onClick: () => {
                onDataObjectSelected && onDataObjectSelected(record.item);
            },
            onFocus: () => {
                onDataObjectSelected && onDataObjectSelected(record.item);
            },
        };
    };

    return (
        <div className="scroll-webkit relative flex size-full flex-1 flex-col overflow-auto">
            <Table
                dataSource={populatedHits}
                size="small"
                rowKey={(hit) => hit.item._dataObjectId}
                loading={loading ? { size: "large", tip: "Getting search result..." } : false}
                className={mergeClassNames("data-object-search-result-list", className)}
                pagination={{
                    position: ["bottomRight"],
                    defaultPageSize: 100,
                    pageSize: 100,
                    responsive: true,
                    showSizeChanger: false,
                    total: totalHits,
                    hideOnSinglePage: true,
                }}
                onChange={(pagination, filters, sorter, extra) => extra.action === "paginate" && onPageSelect && onPageSelect(pagination as IPagination)}
                onRow={(r) => onClickRow(r)}
                tableLayout="auto"
            >
                {[
                    <Column
                        key={"_businessAreaId"}
                        dataIndex={["item", "_businessAreaName"]}
                        title={"Business Area"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        shouldCellUpdate={(record, prevRecord) => {
                            return record !== prevRecord;
                        }}
                        render={(value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />,
                    <Column
                        key={"_schemaId"}
                        dataIndex={["item", "_schemaName"]}
                        title={"Schema"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        shouldCellUpdate={(record, prevRecord) => {
                            return record !== prevRecord;
                        }}
                        render={(value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />,
                    <Column
                        key={"_dataSetId"}
                        dataIndex={["item", "_dataSetName"]}
                        title={"Dataset"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        shouldCellUpdate={(record, prevRecord) => {
                            return record !== prevRecord;
                        }}
                        render={(value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />,
                    <Column
                        key={"_dataObjectId"}
                        dataIndex={["item", "_dataObjectId"]}
                        title={"RecordID"}
                        ellipsis={{
                            showTitle: false,
                        }}
                        shouldCellUpdate={(record, prevRecord) => {
                            return record !== prevRecord;
                        }}
                        render={(value, hit, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                <DataObjectLink dataObjectId={value}></DataObjectLink>
                            </Tooltip>
                        )}
                    />,
                    ...highlightColumns.tagColumns.map((tagColumn) => (
                        <Column
                            key={tagColumn}
                            className="min-w-52"
                            dataIndex={["highlight", tagColumn]}
                            title={<Tag>{tagColumn}</Tag>}
                            shouldCellUpdate={(record, prevRecord) => {
                                return record !== prevRecord;
                            }}
                            render={(value, hit, index) => {
                                if (value) {
                                    return <HighlightValue highlightValues={value}></HighlightValue>;
                                }
                                return <></>;
                            }}
                        />
                    )),
                    ...([...Array(highlightColumns.indexColumnCount)].map((item, index) => {
                        return (
                            <Column
                                key={index}
                                className="min-w-52"
                                dataIndex={["highlight", index]}
                                title={<Text type="secondary" className="whitespace-nowrap">{`Untagged Field(${index + 1})`}</Text>}
                                shouldCellUpdate={(record, prevRecord) => {
                                    return record !== prevRecord;
                                }}
                                render={(value, hit, index) => {
                                    if (value) {
                                        return <HighlightValue highlightValues={value}></HighlightValue>;
                                    }
                                    return <></>;
                                }}
                            />
                        );
                    }) || []),
                ]}
            </Table>
        </div>
    );
}
